import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { Lock, CheckCircle, Eye, EyeOff, Settings, User, Zap, TrendingUp, Clock, Target, ArrowRight, ChevronRight, Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = ({ onLogin, onAdminClick }) => {
  const [showLogin, setShowLogin] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/verify-password`, {
        username: username,
        password: password
      });

      if (response.data.success) {
        localStorage.setItem('go_time_authenticated', 'true');
        localStorage.setItem('go_time_username', response.data.username || username);
        onLogin();
      } else {
        setError('Invalid credentials');
      }
    } catch (err) {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };

  // COVER PAGE
  if (!showLogin) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0a0a0f] relative overflow-hidden px-6">
        {/* Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-[-15%] left-[-5%] w-[550px] h-[550px] bg-gradient-to-r from-teal-600/20 to-cyan-600/20 rounded-full blur-[100px] animate-pulse"></div>
          <div className="absolute bottom-[-15%] right-[-5%] w-[550px] h-[550px] bg-gradient-to-r from-cyan-500/15 to-teal-500/15 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-[45%] left-[45%] w-[350px] h-[350px] bg-gradient-to-r from-emerald-500/15 to-green-500/15 rounded-full blur-[80px] animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        {/* Cover Content */}
        <div className="relative z-10 max-w-lg mx-auto text-center">
          {/* Logo */}
          <div className="mb-5">
            <div className="w-24 h-24 mx-auto relative">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-400 via-cyan-500 to-emerald-500 rounded-2xl rotate-6 opacity-80"></div>
              <div className="absolute inset-0 bg-gradient-to-br from-teal-500 via-cyan-600 to-emerald-600 rounded-2xl flex items-center justify-center shadow-xl shadow-teal-500/30">
                <Zap className="w-12 h-12 text-white" />
              </div>
            </div>
          </div>

          {/* Title */}
          <h1 className="text-5xl font-black text-white mb-2 tracking-tight">
            GO<span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 via-cyan-400 to-emerald-400"> TIME</span>
          </h1>
          <p className="text-gray-400 text-base font-medium uppercase tracking-[0.3em] mb-5">Trading Bot</p>

          {/* Introduction */}
          <div className="bg-white/[0.03] backdrop-blur-sm border border-teal-500/20 rounded-xl p-5 mb-5 text-left">
            <p className="text-gray-300 text-sm leading-relaxed">
              <span className="text-teal-400 font-semibold">Welcome!</span> Your intelligent automated trading companion for Deriv markets. Advanced analysis with smart risk management, auto-recovery, and 24/7 monitoring.
            </p>
          </div>

          {/* Features Row */}
          <div className="grid grid-cols-4 gap-3 mb-6">
            <div className="flex flex-col items-center p-3 rounded-xl bg-teal-500/10 border border-teal-500/20">
              <TrendingUp className="w-6 h-6 text-teal-400 mb-2" />
              <div className="text-white text-xs font-medium">Analysis</div>
            </div>
            <div className="flex flex-col items-center p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
              <Clock className="w-6 h-6 text-cyan-400 mb-2" />
              <div className="text-white text-xs font-medium">24/7</div>
            </div>
            <div className="flex flex-col items-center p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
              <Shield className="w-6 h-6 text-emerald-400 mb-2" />
              <div className="text-white text-xs font-medium">Recovery</div>
            </div>
            <div className="flex flex-col items-center p-3 rounded-xl bg-green-500/10 border border-green-500/20">
              <Target className="w-6 h-6 text-green-400 mb-2" />
              <div className="text-white text-xs font-medium">Accurate</div>
            </div>
          </div>

          {/* Get Started Button */}
          <Button
            onClick={() => setShowLogin(true)}
            className="h-12 px-10 bg-gradient-to-r from-teal-500 via-cyan-500 to-emerald-500 hover:from-teal-600 hover:via-cyan-600 hover:to-emerald-600 text-white font-bold text-base rounded-xl transition-all duration-300 shadow-lg shadow-teal-500/30 hover:shadow-teal-500/50 hover:scale-105"
            data-testid="get-started-button"
          >
            <span className="flex items-center gap-2">
              Get Started
              <ArrowRight className="w-5 h-5" />
            </span>
          </Button>

          {/* Status */}
          <div className="mt-5 flex items-center justify-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
            <span className="text-sm text-gray-500">System Operational</span>
          </div>
        </div>

        {/* Admin Button */}
        <button
          onClick={onAdminClick}
          className="absolute bottom-5 right-5 w-10 h-10 bg-white/[0.05] hover:bg-teal-500/10 border border-teal-500/20 rounded-full flex items-center justify-center transition-all hover:scale-110"
          title="Admin Panel"
          data-testid="admin-link"
        >
          <Settings className="w-5 h-5 text-gray-400" />
        </button>

        <style>{`
          [class*="emergent"], [id*="emergent"], a[href*="emergent"], [data-emergent] { display: none !important; }
        `}</style>
      </div>
    );
  }

  // LOGIN PAGE
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f] relative overflow-hidden px-6">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-[-15%] left-[-5%] w-[450px] h-[450px] bg-gradient-to-r from-teal-600/20 to-cyan-600/20 rounded-full blur-[100px] animate-pulse"></div>
        <div className="absolute bottom-[-15%] right-[-5%] w-[450px] h-[450px] bg-gradient-to-r from-cyan-500/15 to-teal-500/15 rounded-full blur-[100px] animate-pulse"></div>
      </div>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-sm">
        {/* Back Button */}
        <button
          onClick={() => setShowLogin(false)}
          className="flex items-center gap-1 text-gray-400 hover:text-teal-400 mb-5 transition-colors"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          <span className="text-sm">Back</span>
        </button>

        {/* Logo */}
        <div className="text-center mb-5">
          <div className="w-16 h-16 mx-auto mb-3 relative">
            <div className="absolute inset-0 bg-gradient-to-br from-teal-400 via-cyan-500 to-emerald-500 rounded-xl rotate-6 opacity-80"></div>
            <div className="absolute inset-0 bg-gradient-to-br from-teal-500 via-cyan-600 to-emerald-600 rounded-xl flex items-center justify-center">
              <Zap className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-black text-white">
            GO<span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-400"> TIME</span>
          </h1>
        </div>

        {/* Login Form Card */}
        <div className="bg-[#12121a]/90 backdrop-blur-xl border border-teal-500/20 rounded-2xl p-6 shadow-xl">
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-white mb-1">Welcome Back</h2>
            <p className="text-gray-500 text-sm">Enter credentials to start trading</p>
          </div>

          {error && (
            <div className="mb-5 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-2.5 rounded-lg text-sm text-center">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">Username</label>
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Enter username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full bg-white/[0.05] border-teal-500/20 text-white placeholder:text-gray-600 h-11 pl-10 rounded-lg"
                  disabled={loading}
                  data-testid="username-input"
                />
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white/[0.05] border-teal-500/20 text-white placeholder:text-gray-600 h-11 pl-10 pr-10 rounded-lg"
                  disabled={loading}
                  data-testid="password-input"
                />
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-gradient-to-r from-teal-500 via-cyan-500 to-emerald-500 hover:from-teal-600 hover:via-cyan-600 hover:to-emerald-600 text-white font-semibold rounded-lg"
              disabled={loading || !username || !password}
              data-testid="login-submit-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Authenticating...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Access Dashboard
                </span>
              )}
            </Button>
          </form>

          <div className="my-5 flex items-center gap-3">
            <div className="flex-1 h-px bg-teal-500/20"></div>
            <span className="text-gray-600 text-xs">Need access?</span>
            <div className="flex-1 h-px bg-teal-500/20"></div>
          </div>

          <a
            href="https://wa.me/18763758087"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full h-11 rounded-lg bg-[#25D366]/10 hover:bg-[#25D366]/20 border border-[#25D366]/30 text-[#25D366] font-medium transition-all"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Get Access
          </a>

          <div className="mt-5 flex items-center justify-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
            <span className="text-xs text-gray-500">System Operational</span>
          </div>
        </div>
      </div>

      {/* Admin Button */}
      <button
        onClick={onAdminClick}
        className="absolute bottom-5 right-5 w-10 h-10 bg-white/[0.05] hover:bg-teal-500/10 border border-teal-500/20 rounded-full flex items-center justify-center transition-all hover:scale-110"
        title="Admin Panel"
        data-testid="admin-link-login"
      >
        <Settings className="w-5 h-5 text-gray-400" />
      </button>

      <style>{`
        [class*="emergent"], [id*="emergent"], a[href*="emergent"], [data-emergent] { display: none !important; }
      `}</style>
    </div>
  );
};

export default LoginPage;
